import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NewIcon from '@mui/icons-material/Add';
import dateFormat from 'dateformat';
import { Skeleton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { addWorkItem, deleteWorkItem, getWorksHistory, updateWorkItem } from '../../../../actions/cv';
import TitledSection from '../../../common/TitledSection/TitledSection';
import CVBaseItem from '../../../common/CVBaseItem/CVBaseItem';
import classes from './scss/workHistory.module.scss';
import { addNewWork, deleteExistingWork, updateExistingWork } from '../../../../actions/user';
import AddEditCVRecord from '../../../common/ModalWindows/AddEditCVRecord';
import ValidationErrorLabel from '../../../common/ValidationErrorLabel/ValidationErrorLabel';
import colors from '../../../../scss/colors';
import { usePrevious } from '../../../../utils/hooks';

function WorkHistory() {
  const dispatch = useDispatch();
  const historyItems = useSelector((state) => state.cv?.worksHistory?.response);
  const pending = useSelector((state) => state.cv?.updatingPending);

  const prevPending = usePrevious(pending);

  const countCurrentWorks = () => historyItems?.filter((work) => work.CurrentlyWorkThere).length;

  const currentWorkQty = useMemo(() => countCurrentWorks(), [historyItems]);

  const [openAddWindow, setAddWindow] = React.useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getWorksHistory());
  }, []);

  const deleteHandler = (id) => {
    dispatch(deleteWorkItem(id));
    dispatch(deleteExistingWork(id));
  };

  const isNegativeOffset = new Date().getTimezoneOffset() > 0;

  const addWork = async (work) => {
    const start = new Date(work.startdate);
    start.setHours(0);
    const end = new Date(work.enddate);
    end.setHours(0);

    const newWork = {
      CompanyName: work.company,
      Title: work.title,
      Description: work.description,
      CurrentlyWorkThere: work?.currentlyWorkThere || false,
      StartDate: dateFormat(start.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
      EndDate: dateFormat(end.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate')
    };
    dispatch(addWorkItem(newWork));
    dispatch(addNewWork(newWork));
  };

  const updateWork = async (work) => {
    const start = new Date(work.startdate);
    start.setHours(0);
    const end = new Date(work.enddate);
    end.setHours(0);

    const updatedWork = {
      CompanyName: work.company,
      Title: work.title,
      Description: work.description,
      CurrentlyWorkThere: work?.currentlyWorkThere || false,
      StartDate: dateFormat(start.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
      EndDate: dateFormat(end.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
      Id: work.id
    };
    dispatch(updateWorkItem(updatedWork));
    dispatch(updateExistingWork(updatedWork));
  };

  useEffect(() => {
    if (prevPending === true && pending === false) {
      setAddWindow(false);
    }
  }, [pending]);

  return (
    <TitledSection
      title={t('profile.builder.details.history')}
      actions={
        <div className={classes.actionsWrapper}>
          <div className={classes.actionLabel}>
            <span>{t('common.general.save')}</span> <CheckIcon fontSize="8px" />
          </div>
          <NewIcon role="button" className={classes.action} onClick={() => setAddWindow(true)} />
        </div>
      }
    >
      <div className={classes.worksList}>
        {openAddWindow && (
          <AddEditCVRecord
            withCurrentlyCheckBox
            open
            closeHandler={() => setAddWindow(false)}
            editeMode={false}
            confirmHandler={addWork}
            block="works"
            extra={{ currentWorkQty }}
            shouldImmediatelySave
            pending={pending}
          />
        )}
        {historyItems?.length === 0 && (
          <ValidationErrorLabel message={t('dashboard.jobs.emptylist.noworkhistory')} color={colors.NAVY_BLUE} />
        )}
        {historyItems ? (
          historyItems?.map((item) => (
            <CVBaseItem
              withCurrentlyCheckBox
              key={item?.Id || new Date()}
              item={item}
              block="works"
              deleteItem={() => deleteHandler(item?.Id)}
              editItem={updateWork}
              extra={{ currentWorkQty }}
              shouldImmediatelySave
              pending={pending}
            />
          ))
        ) : (
          <Skeleton animation="wave" width="100%" />
        )}
      </div>
    </TitledSection>
  );
}

export default WorkHistory;
